import { useQuery } from "@tanstack/react-query";
import { ApiError, requestJson } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

export type DeliveryOriginSummary = {
  lat: number;
  lng: number;
  line1: string;
  line2: string | null;
  city: string | null;
  country: string | null;
};

export type DeliveryCoverageSummary = {
  radiusM: number;
};

/**
 * Public-facing Delivery session shape. `state` is the high-level
 * availability status:
 *
 *   * `disabled`      — Organization has not enabled Delivery.
 *   * `unconfigured`  — Delivery is enabled but origin or coverage is missing.
 *   * `configured`    — Delivery is enabled and origin + coverage are set.
 *
 * Only `configured` sessions allow a customer to submit a delivery Order.
 * Disabled and unconfigured are intentionally distinct so the UI can show the
 * right blocking message.
 */
export type DeliverySession = {
  state: "configured";
  fulfillmentType: "delivery";
  organizationId: string;
  organizationName: string;
  organizationSlug: string;
  currency: string;
  deliveryMinimumOrder: number;
  deliveryFeeFlat: number;
  cashOnDeliveryAvailable: boolean;
  payNowAvailable: boolean;
  deliveryOrigin: DeliveryOriginSummary;
  deliveryCoverage: DeliveryCoverageSummary;
};

export type DeliverySessionQueryErrorReason =
  | "delivery_disabled"
  | "delivery_unconfigured"
  | string;

type DeliverySessionErrorPayload = {
  reason?: DeliverySessionQueryErrorReason;
};

export function useDeliverySession(orgSlug?: string) {
  return useQuery<DeliverySession, ApiError>({
    queryKey: queryKeys.deliverySession.byOrgSlug(orgSlug),
    queryFn: () =>
      requestJson<DeliverySession>("/api/orders/delivery-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orgSlug }),
      }),
    enabled: Boolean(orgSlug),
    staleTime: 30_000,
  });
}

/**
 * Inspect the error from `useDeliverySession` and report the high-level
 * availability reason. Used by checkout UI to render distinct messages for
 * disabled vs unconfigured states. Reads the structured reason code emitted
 * by the backend (`data.reason`) and only falls back to status-code/message
 * heuristics when the structured code is missing.
 */
export function classifyDeliverySessionError(
  error: ApiError | null,
): "ok" | "disabled" | "unconfigured" | "unknown" {
  if (!error) {
    return "ok";
  }
  const reason = (error.data as DeliverySessionErrorPayload | undefined)?.reason;
  if (reason === "delivery_disabled") {
    return "disabled";
  }
  if (reason === "delivery_unconfigured") {
    return "unconfigured";
  }
  if (error.statusCode === 422) {
    if (/not offering delivery/i.test(error.message)) {
      return "disabled";
    }
    if (/not finished setting up delivery/i.test(error.message)) {
      return "unconfigured";
    }
  }
  return "unknown";
}
